import React, { useCallback, useEffect, useRef, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, IconButton, Stack, Tab, Tabs } from '@mui/material';

import { FormToc } from 'components/console';
import TemplateGroupView, { TemplateGroup } from 'pages/contract/components/TemplateGroupView';
import { MasterTypeResult } from 'stores/MasterTypeStoreAdapter';
import {
  TicketDetailFormGroup,
  TicketDetailFormItem,
  TicketDetailResult,
  getTicket,
} from 'stores/TicketStoreAdapter';
import { TicketTypeSummaryResult, listTicketType } from 'stores/TicketTypeStoreAdapter';
import { errorDefault } from 'utils/Toastify';

import RelatedTicketsTab from './RelatedTicketsTab';
import TicketCommentSection from './TicketCommentSection';
import TicketCreateDialog from './TicketCreateDialog';
import TicketDetail from './TicketDetail';
import TicketEditDialog from './TicketEditDialog';

type Props = {
  detail: TicketDetailResult;
  priorities: MasterTypeResult[];
  /** 詳細を再取得して差し替える */
  onReload: () => void;
  /** 関連チケットへ遷移 */
  onOpenTicket: (ticketId: string) => void;
  /** 指定時、上部に閉じる導線を表示（direct-link時） */
  onBack?: () => void;
};

/**
 * チケット詳細ビュー（本文 + 右目次 + 下部タブ）。
 * ドロワー / direct-link で密度違いに共有する。
 */
const TicketDetailView: React.FC<Props> = ({
  detail,
  priorities,
  onReload,
  onOpenTicket,
  onBack,
}) => {
  // コメント/関連チケットタブ。null = 未選択（最小化）。
  // - タブクリック: 同じタブなら未選択(最小化)、違うタブなら選択(最大化)
  // - 最大化ボタン: 最大化 + 先頭タブ選択 / 最小化ボタン: 最小化 + 未選択
  const [selectedTab, setSelectedTab] = useState<number | null>(null);
  const expanded = selectedTab !== null;
  const handleTabClick = (index: number) =>
    setSelectedTab((prev) => (prev === index ? null : index));
  // 折りたたみアニメーション中も直前の内容を表示し続けるため、最後に選択したタブを保持する
  const contentTabRef = useRef(0);
  if (selectedTab !== null) contentTabRef.current = selectedTab;
  const contentTab = contentTabRef.current;
  const [editOpen, setEditOpen] = useState(false);
  const [childOpen, setChildOpen] = useState(false);
  const [activeTypes, setActiveTypes] = useState<TicketTypeSummaryResult[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 子チケット作成のため有効種別を取得
  useEffect(() => {
    listTicketType({ activeOnly: true })
      .then(setActiveTypes)
      .catch(() => setActiveTypes([]));
  }, []);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {onBack && (
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            onClick={onBack}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            閉じる
          </Button>
        </Stack>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <Box ref={scrollRef} sx={{ flex: 1, minWidth: 0, overflow: 'auto', px: 2, pt: 1 }}>
          <TicketDetail
            detail={detail}
            priorities={priorities}
            onUpdated={onReload}
            onEdit={() => setEditOpen(true)}
            onOpenTicket={onOpenTicket}
          />
          <FormValuesView formGroups={detail.formGroups} unassignedItems={detail.unassignedItems} />
        </Box>
        <FormToc scrollRef={scrollRef} deps={[detail.ticketId]} />
      </Box>

      {/* 下部タブ（コメント / 関連チケット） */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" sx={{ px: 2 }}>
          <Tabs value={selectedTab ?? false} sx={{ minHeight: 36, flex: 1 }}>
            <Tab
              label="コメント"
              onClick={() => handleTabClick(0)}
              sx={{ textTransform: 'none', minHeight: 36, py: 0 }}
            />
            <Tab
              label="関連チケット"
              onClick={() => handleTabClick(1)}
              sx={{ textTransform: 'none', minHeight: 36, py: 0 }}
            />
          </Tabs>
          <IconButton
            size="small"
            aria-label={expanded ? '最小化' : '最大化'}
            onClick={() => setSelectedTab(expanded ? null : 0)}
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Collapse in={expanded} timeout={250} unmountOnExit>
          <Box sx={{ p: 2, height: 360, overflow: 'auto' }}>
            {contentTab === 0 && <TicketCommentSection ticketId={detail.ticketId} />}
            {contentTab === 1 && (
              <RelatedTicketsTab
                detail={detail}
                onOpenTicket={onOpenTicket}
                onCreateChild={() => setChildOpen(true)}
              />
            )}
          </Box>
        </Collapse>
      </Box>

      {/* 編集ダイアログ */}
      <TicketEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onUpdated={onReload}
        detail={detail}
        priorities={priorities}
      />

      {/* 子チケット作成 */}
      <TicketCreateDialog
        open={childOpen}
        onClose={() => setChildOpen(false)}
        onCreated={onReload}
        activeTypes={activeTypes}
        priorities={priorities}
        parent={{
          ticketId: detail.ticketId,
          typeId: detail.typeId,
          managementId: detail.managementId,
        }}
      />
    </Box>
  );
};

/**
 * 入力済みフォーム値の参照表示。
 * 申請フォーム設定/申請詳細と共通の TemplateGroupView（mode=preview）で描画し、
 * TypeCode（formType）に応じた表示をアプリ内で共通化する。
 */
const FormValuesView: React.FC<{
  formGroups: TicketDetailFormGroup[];
  unassignedItems: TicketDetailFormItem[];
}> = ({ formGroups, unassignedItems }) => {
  const hasAny = formGroups.some((g) => g.items.length > 0) || unassignedItems.length > 0;
  if (!hasAny) return null;

  // TicketDetailFormGroup/Item → TemplateGroup へ変換（値は itemKey→string マップ）
  const toItems = (items: TicketDetailFormItem[]) =>
    items.map((it) => ({
      itemKey: it.itemKey,
      label: it.label,
      formType: it.formType,
      // detail は選択肢マスタを持たないため、コード値をそのまま表示（名称解決は種別テンプレ側）
      selectItems: [] as { code: string; name: string }[],
    }));

  const groups: TemplateGroup[] = [
    ...formGroups
      .filter((g) => g.items.length > 0)
      .map((g) => ({ groupId: g.groupId, name: g.name, items: toItems(g.items) })),
  ];
  if (unassignedItems.length > 0) {
    groups.push({ groupId: '__unassigned__', name: 'その他項目', items: toItems(unassignedItems) });
  }

  // 値マップ（MULTI 系は JSON 配列文字列で TemplateGroupView に渡す）
  const MULTI = ['MULTI_SELECT', 'MULTI_CHECK', 'MULTI_CHECK_BUTTON', 'TECH_STACK_SELECT'];
  const values: Record<string, string> = {};
  const collect = (items: TicketDetailFormItem[]) =>
    items.forEach((it) => {
      values[it.itemKey] = MULTI.includes(it.formType)
        ? JSON.stringify(it.values ?? [])
        : ((it.values && it.values[0]) ?? '');
    });
  formGroups.forEach((g) => collect(g.items));
  collect(unassignedItems);

  return (
    <Box sx={{ mt: 2 }} data-toc-section id="ticket-form-values" data-toc-label="入力項目">
      <TemplateGroupView groups={groups} mode="preview" values={values} onValueChange={() => {}} />
    </Box>
  );
};

export default TicketDetailView;

/** ドロワー/direct-link 共通の詳細ローダー（ticketId から取得して表示） */
export const useTicketDetail = () => {
  const [detail, setDetail] = useState<TicketDetailResult | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback((ticketId: string) => {
    setLoading(true);
    getTicket({ ticketId })
      .then(setDetail)
      .catch(() => errorDefault('チケットの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  const clear = useCallback(() => setDetail(null), []);

  return { detail, loading, load, clear, setDetail };
};
